/*
 * The User dropdown (profile / notifications) for the redesigned global nav.
 *
 * Contained in the code is essentially a messy hybrid of ProfileDropdown
 * and NotificationsDropDown.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {markMinorConversions} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import withNotifications, {
    type WithNotificationsProps,
    type WithoutNotifications,
} from "#notifications-package/with-notifications.tsx";

import DropdownButton from "./dropdown-button.tsx";
import HeaderDropdown from "./header-dropdown.tsx";
import NotificationsBadge from "./notifications-badge.tsx";
import NotificationsList from "./notifications-list.tsx";
import NotificationsSwitch from "./notifications-switch.tsx";
import TransitionAppear from "./transition-appear.tsx";
import UserSettings from "./user-settings.tsx";

import caretDownIcon from "@phosphor-icons/core/fill/caret-down-fill.svg";
import caretUpIcon from "@phosphor-icons/core/fill/caret-up-fill.svg";

export type ProfileDropdownData = // Get all the props from UserSettings except
    // navigateToNotificationsSwitch
    Omit<
        JSX.LibraryManagedAttributes<
            typeof UserSettings,
            React.ComponentProps<typeof UserSettings>
        >,
        "navigateToNotificationsSwitch"
    > & {
        // add these missing props
        nickName?: string;
        showTeacherLinks?: boolean;
        showParentLinks?: boolean;
        showAdministratorLinks?: boolean;
        showKhanAcademyClassroom: boolean;
    };

type Props = {
    // Extra styles to apply to the dropdown trigger.
    buttonStyles: StyleType;
    // The current URL. Used to generate the logout link.
    currentUrl?: string;
    // A function called when the dropdown trigger is pressed.
    onDropdownToggle: () => unknown;
    // A function called when the dropdown is closed.
    onDropdownClose: () => unknown;
    // TODO(WEB-1695): Use withUserProfile instead
    // An exact object type containing the required profileData
    profileData?: ProfileDropdownData | null | undefined;
    // Whether the dropdown should be open or not.
    showDropdown: boolean;
    // Whether we should show the notifications view initially
    showNotifications?: boolean;
    signUpUrl?: string;
    // Callback to be invoked when the root element is mounted.
    dropdownRef: (arg1?: HTMLElement | null | undefined) => unknown;
    // Whether the background color is white
    isLightTheme?: boolean;
} & WithNotificationsProps;

type DefaultProps = {
    showDropdown: Props["showDropdown"];
    onDropdownToggle: Props["onDropdownToggle"];
    onDropdownClose: Props["onDropdownClose"];
    dropdownRef: Props["dropdownRef"];
    buttonStyles: Props["buttonStyles"];
};

type State = {
    showNotifications: boolean;
};

class UserDropdown extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        showDropdown: false,
        onDropdownToggle: () => {},
        onDropdownClose: () => {},
        dropdownRef: () => {},
        buttonStyles: [],
    };

    state: State = {
        showNotifications: !!this.props.showNotifications,
    };

    // Invoke the callback when we open notifications either initially or by
    // toggling the popup.
    componentDidMount() {
        if (this.props.showNotifications) {
            this.props.clearNotifications();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevState.showNotifications && this.state.showNotifications) {
            this.props.clearNotifications();
            markMinorConversions([
                {
                    id: "global_nav_open_notifications",
                    extra: {
                        view: "desktop",
                    },
                },
            ]);
        }
    }

    dropdownButton: Element | null | undefined = null;

    firstNavigableChild: HTMLElement | null | undefined = null;

    notificationsSwitch: HTMLElement | null | undefined = null;

    hasLoadedNotifications = false;

    focusDropdownButton: () => void = () => {
        if (this.dropdownButton) {
            /**
             * TODO(somewhatabstract, JIRA-XXXX):
             * Element doesn't have a `focus` method.
             */
            // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.
            this.dropdownButton.focus();
        }
    };

    handleDropdownButtonClick: () => void = () => {
        this.props.onDropdownToggle();

        // Keep track of whether we've run the `loadNotifications` function
        // before, so we only call it once from the user opening the dropdown.
        if (!this.hasLoadedNotifications) {
            this.props.loadNotifications();
            this.hasLoadedNotifications = true;
        }
    };

    handleDropdownClose: () => void = () => {
        this.focusDropdownButton();
        this.props.onDropdownClose();
    };

    handleScroll: (e: React.UIEvent<HTMLLIElement>) => void = (e) => {
        if (
            !this.state.showNotifications ||
            !(e.currentTarget instanceof HTMLElement)
        ) {
            return;
        }

        const {scrollHeight, clientHeight, scrollTop} = e.currentTarget;
        const offset = 2;

        // Calculate when the user has scrolled to the bottom of the dropdown.
        if (scrollTop + clientHeight >= scrollHeight - offset) {
            this.props.loadNotifications();

            e.preventDefault();
        }
    };

    // Because this is a dropdown menu, we want users to be able to navigate
    // to the menu's contents from the dropdown button with the down arrow (but
    // only if the menu is already open).
    handleNavigateToNotificationsSwitch: (event: React.KeyboardEvent) => void =
        (event) => {
            const {notificationsSwitch} = this;

            if (this.props.showDropdown && notificationsSwitch) {
                event.preventDefault();
                notificationsSwitch.focus();
            } else if (!this.props.showDropdown) {
                event.preventDefault();
                this.props.onDropdownToggle();
            }
        };

    handleToggleClick: () => void = () => {
        // TODO(jordan): Transition between the two screens
        this.setState({
            showNotifications: !this.state.showNotifications,
        });
    };

    handleArrowDownNotificationsSwitch: () => void = () => {
        if (
            this.firstNavigableChild &&
            this.firstNavigableChild.firstChild &&
            this.firstNavigableChild.firstChild instanceof HTMLElement
        ) {
            this.firstNavigableChild.firstChild.focus();
        }
    };

    handleArrowUpNotificationsSwitch: () => void = () => {
        this.handleDropdownClose();
    };

    handleArrowClose: (event: React.KeyboardEvent) => void = (event) => {
        event.preventDefault();
        this.handleDropdownClose();
    };

    render(): React.ReactNode {
        const showNotifications = this.state.showNotifications;
        const {
            newNotificationsCount,
            notifications,
            notificationsLoading,
            profileData,
        } = this.props;

        const showDonateLink = RequestInfo.kaLocale === "en";

        // NOTE(diedra): This role is used to tell screen readers that this
        // element is part of the site's navigation. If you refactor this code,
        // please leave this (unless you turn it into a `nav` element instead of
        // a `div` - that works too.)
        return (
            <div
                aria-label={t`user menu`}
                role="navigation"
                className={css(styles.wrapper)}
                ref={this.props.dropdownRef}
            >
                <DropdownButton
                    aria-label={profileData?.nickName || t`user menu`}
                    dropdownIsOpen={this.props.showDropdown}
                    innerRef={(node) => (this.dropdownButton = node)}
                    buttonStyles={this.props.buttonStyles}
                    onClick={this.handleDropdownButtonClick}
                    onArrowDown={this.handleNavigateToNotificationsSwitch}
                    onArrowUp={this.handleArrowClose}
                    onEscape={this.handleDropdownClose}
                    testId="user-dropdown"
                >
                    <span
                        className={css(
                            styles.trigger,
                            showDonateLink && styles.shortTrigger,
                        )}
                        data-testid="header-profile-button"
                    >
                        {profileData && profileData.nickName && (
                            <TransitionAppear>
                                <>
                                    <LabelLarge style={styles.nickname}>
                                        {profileData.nickName}
                                    </LabelLarge>
                                    <PhosphorIcon
                                        style={[
                                            !this.props.isLightTheme &&
                                                styles.transparentTriangle,
                                            newNotificationsCount > 0
                                                ? styles.triangleWithNotifications
                                                : styles.triangleWithoutNotifications,
                                        ]}
                                        icon={
                                            this.props.showDropdown
                                                ? caretUpIcon
                                                : caretDownIcon
                                        }
                                        size="small"
                                    />
                                </>
                            </TransitionAppear>
                        )}

                        {newNotificationsCount > 0 && (
                            <NotificationsBadge
                                size="medium"
                                style={styles.notificationsBadge}
                            />
                        )}
                    </span>
                </DropdownButton>
                <HeaderDropdown
                    visible={this.props.showDropdown}
                    hasBorder={false}
                    dropdownStyles={[styles.dropdown]}
                >
                    <NotificationsSwitch
                        innerRef={(node) => (this.notificationsSwitch = node)}
                        direction={showNotifications ? "left" : "right"}
                        onClick={this.handleToggleClick}
                        onArrowDown={this.handleArrowDownNotificationsSwitch}
                        onArrowUp={this.handleArrowUpNotificationsSwitch}
                        onEscape={this.handleDropdownClose}
                        notificationCount={newNotificationsCount}
                    />

                    <li
                        className={css(
                            showNotifications && styles.scrollDropdown,
                        )}
                        onScroll={this.handleScroll}
                    >
                        {showNotifications ? (
                            <NotificationsList
                                notifications={notifications}
                                loading={notificationsLoading}
                                onDropdownClose={this.handleDropdownClose}
                                navigateToNotificationsSwitch={
                                    this.handleNavigateToNotificationsSwitch
                                }
                                captureFirstNavigableChildRef={(input) =>
                                    (this.firstNavigableChild = input)
                                }
                            />
                        ) : (
                            // @ts-expect-error - TS2322 - Type '{ signUpUrl: string | undefined; currentUrl: string | undefined; onCloseDropdown: () => void; navigateToNotificationsSwitch: (event: KeyboardEvent<Element>) => void; ... 13 more ...; nickName?: string | undefined; }' is not assignable to type 'ExportProps'.
                            <UserSettings
                                {...this.props.profileData}
                                signUpUrl={this.props.signUpUrl}
                                currentUrl={this.props.currentUrl}
                                onCloseDropdown={this.handleDropdownClose}
                                navigateToNotificationsSwitch={
                                    this.handleNavigateToNotificationsSwitch
                                }
                                captureFirstNavigableChildRef={(input) =>
                                    (this.firstNavigableChild = input)
                                }
                            />
                        )}
                    </li>
                </HeaderDropdown>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    wrapper: {
        display: "inline-block",
        height: "100%",
        position: "relative",

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    trigger: {
        maxWidth: 205,
        position: "relative",
        whiteSpace: "nowrap",
    },

    // Applied when the Donate button is added to the
    // header, giving less room for the username trigger.
    // This is really only applicable for the 768-1024
    // screen size, so the maxWidth is only reduced for
    // those screen dimensions.
    shortTrigger: {
        [mediaQueries.mdOrLarger]: {
            maxWidth: 115,
        },
        [mediaQueries.lgOrLarger]: {
            maxWidth: 175,
        },
    },

    nickname: {
        display: "inline-block",
        maxWidth: "85%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    },

    // Lots of magic numbers here!
    notificationsBadge: {
        position: "absolute",
        right: 15,
        top: -7,
    },

    dropdown: {
        backgroundColor: color.offWhite,
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
        right: -8,
        width: 300,
    },

    scrollDropdown: {
        height: 220,
        overflowY: "scroll",
    },

    triangleWithNotifications: {
        display: "inline-block",
        marginLeft: spacing.xSmall_8,
        position: "relative",
        verticalAlign: "middle",
    },

    triangleWithoutNotifications: {
        display: "inline-block",
        marginLeft: spacing.xxSmall_6,
        position: "relative",
        verticalAlign: "middle",
    },

    transparentTriangle: {
        color: "rgba(255, 255, 255, 0.48)",
    },
});

type ExportProps = WithoutNotifications<
    JSX.LibraryManagedAttributes<
        typeof UserDropdown,
        React.ComponentProps<typeof UserDropdown>
    >
>;

export default withNotifications(
    UserDropdown,
) as React.ComponentType<ExportProps>;
